import { ENDPOINTS } from '@/config/endpoints'

const DEFAULT_OFFER_ID = 320
const SDK_LOAD_TIMEOUT_MS = 4000
const SDK_REMOTE_RETRY_COUNT = 2
const CLICK_TIMEOUT_MS = 8000
const CLICK_RECOVERY_ATTEMPTS = 10
const CLICK_RECOVERY_DELAY_MS = 300
const CLICK_RETRY_DELAY_MS = 500
const STORED_TRANSACTION_MAX_AGE_MS = 60 * 60 * 1000
const STORAGE_PREFIX = 'everflow_transaction'
const DEBUG_LOG_PREFIX = 'everflow_debug_log'
const SDK_ERROR_PREFIX = 'everflow_sdk_error'
const CLICK_ERROR_PREFIX = 'everflow_click_error'
const DEBUG_LOG_MAX_LENGTH = 250

let everflowSdkPromise = null

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function getStorageKey({ affId, offerId }) {
  return `${STORAGE_PREFIX}:${affId}:${offerId}`
}

function getDebugLogKey({ affId, offerId }) {
  return `${DEBUG_LOG_PREFIX}:${affId}:${offerId}`
}

function getSdkErrorKey({ affId, offerId }) {
  return `${SDK_ERROR_PREFIX}:${affId}:${offerId}`
}

function getClickErrorKey({ affId, offerId }) {
  return `${CLICK_ERROR_PREFIX}:${affId}:${offerId}`
}

function getLocationSignature() {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.search}`
}

function readSessionStorage(key) {
  if (typeof window === 'undefined') return ''

  try {
    return window.sessionStorage.getItem(key) || ''
  } catch {
    return ''
  }
}

function writeSessionStorage(key, value) {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Ignore browsers that block sessionStorage access.
  }
}

function removeSessionStorage(key) {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Ignore browsers that block sessionStorage access.
  }
}

function parseTransactionId(value) {
  if (!value || typeof value !== 'string') return ''
  const parts = value.split('|').map((part) => part.trim()).filter(Boolean)
  return parts.length ? parts[parts.length - 1] : value.trim()
}

function getParamValue(params, ...keys) {
  for (const key of keys) {
    const value = params.get(key)
    if (value) {
      return value
    }
  }

  return ''
}

function getCookieValue(name) {
  if (typeof document === 'undefined') return ''
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : ''
}

function recoverTransactionIdFromCookies({ affId, offerId }) {
  const advertiserTransactionId = parseTransactionId(getCookieValue(`ef_tid_c_a_${affId}`)) ||
    parseTransactionId(getCookieValue(`ef_tid_i_a_${affId}`))

  if (advertiserTransactionId) {
    return {
      transactionId: advertiserTransactionId,
      source: 'ca'
    }
  }

  const offerTransactionId = parseTransactionId(getCookieValue(`ef_tid_c_o_${offerId}`)) ||
    parseTransactionId(getCookieValue(`ef_tid_i_o_${offerId}`)) ||
    parseTransactionId(getCookieValue(`ef_tid_${offerId}`))

  if (offerTransactionId) {
    return {
      transactionId: offerTransactionId,
      source: 'co'
    }
  }

  return {
    transactionId: '',
    source: ''
  }
}

function buildClickOptions({ affId, offerId, params }) {
  return {
    offer_id: offerId,
    affiliate_id: affId,
    sub1: getParamValue(params, 's1', 'sub1'),
    sub2: getParamValue(params, 'reid', 's2', 'sub2'),
    sub3: getParamValue(params, 's3', 'sub3'),
    sub4: getParamValue(params, 's4', 'sub4'),
    sub5: getParamValue(params, 's5', 'sub5'),
  }
}

function normalizeOfferId(offerId) {
  return offerId || DEFAULT_OFFER_ID
}

function mapClickStatusToCode(status) {
  switch (status) {
    case 'success':
      return 'ok'
    case 'empty':
      return 'em'
    case 'timeout':
      return 'to'
    case 'network_error':
      return 'ne'
    case 'http_error':
      return 'he'
    case 'json_error':
      return 'js'
    case 'error':
      return 'er'
    default:
      return 'uk'
  }
}

function mapSdkErrorToCode(error) {
  const message = error?.message || ''

  if (message.includes('timed out')) return 'to'
  if (message.includes('without EF')) return 'nf'
  return 'er'
}

function trimDebugLogEntries(entries) {
  const parts = [...entries]

  while (parts.length > 1 && parts.join('|').length > DEBUG_LOG_MAX_LENGTH) {
    parts.splice(1, 1)
  }

  const serialized = parts.join('|')
  return serialized.length <= DEBUG_LOG_MAX_LENGTH
    ? serialized
    : serialized.slice(0, DEBUG_LOG_MAX_LENGTH)
}

function getStoredScopedValue(key) {
  const storedValue = readSessionStorage(key)
  if (!storedValue) return ''

  try {
    const parsed = JSON.parse(storedValue)
    const isCurrentLocation = parsed.locationSignature === getLocationSignature()
    const isFresh = Date.now() - Number(parsed.storedAt || 0) <= STORED_TRANSACTION_MAX_AGE_MS

    if (!isCurrentLocation || !isFresh || !parsed.value) {
      removeSessionStorage(key)
      return ''
    }

    return String(parsed.value)
  } catch {
    removeSessionStorage(key)
    return ''
  }
}

function writeScopedValue(key, value) {
  if (typeof window === 'undefined' || !value) return

  writeSessionStorage(
    key,
    JSON.stringify({
      value,
      locationSignature: getLocationSignature(),
      storedAt: Date.now()
    })
  )
}

function getStoredDebugLog({ affId, offerId }) {
  if (typeof window === 'undefined') return ''

  const normalizedOfferId = normalizeOfferId(offerId)
  return getStoredScopedValue(getDebugLogKey({ affId, offerId: normalizedOfferId }))
}

function writeDebugLog({ affId, offerId, log }) {
  if (typeof window === 'undefined' || !log) return

  const normalizedOfferId = normalizeOfferId(offerId)

  writeScopedValue(getDebugLogKey({ affId, offerId: normalizedOfferId }), log)
}

export function getEverflowDebugLog({ affId, offerId }) {
  if (!affId) return ''
  return getStoredDebugLog({ affId, offerId })
}

export function appendEverflowDebugLog({ affId, offerId, entry }) {
  if (!affId || !entry) return

  const sanitizedEntry = String(entry).replace(/\|/g, '').trim()
  if (!sanitizedEntry) return

  const existingEntries = getStoredDebugLog({ affId, offerId })
    .split('|')
    .filter(Boolean)

  existingEntries.push(sanitizedEntry)

  writeDebugLog({
    affId,
    offerId,
    log: trimDebugLogEntries(existingEntries)
  })
}

export function clearEverflowDebugLog({ affId, offerId }) {
  if (!affId) return
  removeSessionStorage(getDebugLogKey({ affId, offerId: normalizeOfferId(offerId) }))
}

function sanitizeSdkErrorMessage(message) {
  return String(message || '')
    .replace(/\|/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function getStoredSdkError({ affId, offerId }) {
  if (typeof window === 'undefined') return ''

  const normalizedOfferId = normalizeOfferId(offerId)
  return getStoredScopedValue(getSdkErrorKey({ affId, offerId: normalizedOfferId }))
}

function writeSdkError({ affId, offerId, value }) {
  if (typeof window === 'undefined' || !value) return

  const normalizedOfferId = normalizeOfferId(offerId)
  writeScopedValue(getSdkErrorKey({ affId, offerId: normalizedOfferId }), value)
}

export function getEverflowSdkError({ affId, offerId }) {
  if (!affId) return ''
  return getStoredSdkError({ affId, offerId })
}

export function appendEverflowSdkError({ affId, offerId, sourceLabel, error }) {
  if (!affId || !sourceLabel || !error?.message) return

  const existingEntries = getStoredSdkError({ affId, offerId })
    .split('|')
    .filter(Boolean)

  existingEntries.push(`${sourceLabel}:${sanitizeSdkErrorMessage(error.message)}`)

  writeSdkError({
    affId,
    offerId,
    value: trimDebugLogEntries(existingEntries)
  })
}

export function clearEverflowSdkError({ affId, offerId }) {
  if (!affId) return
  removeSessionStorage(getSdkErrorKey({ affId, offerId: normalizeOfferId(offerId) }))
}

function getStoredClickError({ affId, offerId }) {
  if (typeof window === 'undefined') return ''

  const normalizedOfferId = normalizeOfferId(offerId)
  return getStoredScopedValue(getClickErrorKey({ affId, offerId: normalizedOfferId }))
}

function writeClickError({ affId, offerId, value }) {
  if (typeof window === 'undefined' || !value) return

  const normalizedOfferId = normalizeOfferId(offerId)
  writeScopedValue(getClickErrorKey({ affId, offerId: normalizedOfferId }), value)
}

export function getEverflowClickError({ affId, offerId }) {
  if (!affId) return ''
  return getStoredClickError({ affId, offerId })
}

export function appendEverflowClickError({ affId, offerId, sourceLabel, error }) {
  if (!affId || !sourceLabel || !error?.message) return

  const existingEntries = getStoredClickError({ affId, offerId })
    .split('|')
    .filter(Boolean)

  existingEntries.push(`${sourceLabel}:${sanitizeSdkErrorMessage(error.message)}`)

  writeClickError({
    affId,
    offerId,
    value: trimDebugLogEntries(existingEntries)
  })
}

export function clearEverflowClickError({ affId, offerId }) {
  if (!affId) return
  removeSessionStorage(getClickErrorKey({ affId, offerId: normalizeOfferId(offerId) }))
}

function getEverflowClickEndpoint() {
  return new URL('/sdk/click', ENDPOINTS.EVERFLOW_SDK).toString()
}

function setCookieValue(name, value, hours = 720) {
  if (typeof document === 'undefined') return

  const expiresAt = new Date()
  expiresAt.setTime(expiresAt.getTime() + 60 * hours * 60 * 1000)

  let normalizedValue = String(value)
  if (normalizedValue.length > 1650) {
    normalizedValue = `${normalizedValue.substring(0, 33)}${normalizedValue.substring(normalizedValue.length - 1616)}`
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(normalizedValue)};expires=${expiresAt.toUTCString()};path=/`
}

function appendTransactionCookie(name, transactionId) {
  const currentValue = getCookieValue(name)
  const nextValue = currentValue ? `${currentValue}|${transactionId}` : transactionId
  setCookieValue(name, nextValue)
}

function buildEverflowClickRequestUrl({ affId, offerId, params }) {
  const clickOptions = buildClickOptions({ affId, offerId, params })
  const url = new URL(getEverflowClickEndpoint())
  const query = new URLSearchParams(url.search)
  const queryKeys = Array.from(params.keys())
  const existingSession = getCookieValue(`ef_session_${offerId}`)
  const randomNumber = Math.floor(Math.random() * 100)

  query.set('_ef_transaction_id', '')
  query.set('oid', String(offerId))
  query.set('affid', String(affId))
  query.set('__cc', '')
  query.set('async', 'json')

  Object.entries(clickOptions).forEach(([key, value]) => {
    if (value) {
      query.set(key, value)
    }
  })

  const fbclid = getParamValue(params, 'fbclid')
  const gclid = getParamValue(params, 'gclid')
  const ttclid = getParamValue(params, 'ttclid')
  const sccid = getParamValue(params, 'ScCid', 'sccid')

  if (fbclid) query.set('fbclid', fbclid)
  if (gclid) query.set('gclid', gclid)
  if (ttclid) query.set('ttclid', ttclid)
  if (sccid) query.set('sccid', sccid)

  if (queryKeys.length) {
    query.set('__qp', queryKeys.join('|'))
  }

  if (typeof document !== 'undefined' && document.referrer) {
    query.set('__rf', document.referrer)
  }

  query.set('__efckuq', existingSession
    ? String((2 * randomNumber) + 1)
    : String(randomNumber % 2 === 0 ? randomNumber : randomNumber + 1))

  url.search = query.toString()
  return url.toString()
}

function persistEverflowClickResponse({ affId, offerId, responseData, transactionId }) {
  const responseOfferId = responseData?.oid || offerId
  const responseAffId = responseData?.aid || affId
  const sessionDuration = Number(responseData?.session_duration) > 0
    ? Number(responseData.session_duration)
    : 720

  setCookieValue('ef_witness', '1')
  setCookieValue('ef_affid', String(affId || ''))
  setCookieValue(`ef_session_${responseOfferId}`, '1', sessionDuration)
  appendTransactionCookie(`ef_tid_c_o_${responseOfferId}`, transactionId)
  appendTransactionCookie(`ef_tid_c_a_${responseAffId}`, transactionId)
}

function withTimeout(promise, timeoutMs, message) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(message))
    }, timeoutMs)

    promise.then((value) => {
      clearTimeout(timeoutId)
      resolve(value)
    }).catch((error) => {
      clearTimeout(timeoutId)
      reject(error)
    })
  })
}

function loadEverflowSdkOnce(src) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(null)
      return
    }

    if (window.EF) {
      resolve(window.EF)
      return
    }

    const script = document.createElement('script')
    const cleanup = () => {
      clearTimeout(timeoutId)
      script.onload = null
      script.onerror = null
    }

    const timeoutId = setTimeout(() => {
      cleanup()
      script.remove()
      reject(new Error('Everflow SDK load timed out'))
    }, SDK_LOAD_TIMEOUT_MS)

    script.type = 'text/javascript'
    script.async = true
    script.src = src

    script.onload = function() {
      cleanup()

      if (window.EF) {
        resolve(window.EF)
        return
      }

      script.remove()
      reject(new Error('Everflow SDK loaded without EF'))
    }

    script.onerror = function() {
      cleanup()
      script.remove()
      reject(new Error('Everflow SDK failed to load'))
    }

    document.getElementsByTagName('head')[0].appendChild(script)
  })
}

async function loadEverflowSdk({ affId, offerId }) {
  if (typeof window === 'undefined') {
    return null
  }

  if (window.EF) {
    appendEverflowDebugLog({ affId, offerId, entry: 'se' })
    return window.EF
  }

  if (everflowSdkPromise) {
    return everflowSdkPromise
  }

  everflowSdkPromise = (async () => {
    const sdkSources = [
      ...Array.from({ length: SDK_REMOTE_RETRY_COUNT }, (_, index) => ({
        label: `sr${index + 1}`,
        src: ENDPOINTS.EVERFLOW_SDK
      })),
      {
        label: 'sl',
        src: ENDPOINTS.EVERFLOW_SDK_FALLBACK
      }
    ]
    let lastError = null

    for (let attempt = 0; attempt < sdkSources.length; attempt++) {
      const source = sdkSources[attempt]

      try {
        const ef = await loadEverflowSdkOnce(source.src)
        appendEverflowDebugLog({ affId, offerId, entry: `${source.label}ok` })
        return ef
      } catch (error) {
        lastError = error
        appendEverflowSdkError({
          affId,
          offerId,
          sourceLabel: source.label,
          error
        })
        appendEverflowDebugLog({
          affId,
          offerId,
          entry: `${source.label}${mapSdkErrorToCode(error)}`
        })

        if (attempt < sdkSources.length - 1) {
          await wait((attempt + 1) * CLICK_RECOVERY_DELAY_MS)
        }
      }
    }

    appendEverflowDebugLog({ affId, offerId, entry: 'sf' })
    throw lastError || new Error('Everflow SDK failed to load')
  })()

  try {
    return await everflowSdkPromise
  } catch (error) {
    everflowSdkPromise = null
    throw error
  }
}

async function attemptEverflowClick({ affId, offerId, params }) {
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = setTimeout(() => {
      controller?.abort()
    }, CLICK_TIMEOUT_MS)

    let response
    try {
      response = await fetch(buildEverflowClickRequestUrl({ affId, offerId, params }), {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: controller?.signal
      })
    } finally {
      clearTimeout(timeoutId)
    }

    if (!response.ok) {
      return {
        transactionId: '',
        status: 'http_error',
        errorDetail: `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`
      }
    }

    const responseText = await response.text()
    let responseData

    try {
      responseData = responseText ? JSON.parse(responseText) : null
    } catch {
      return {
        transactionId: '',
        status: 'json_error',
        errorDetail: 'Invalid JSON'
      }
    }

    const parsedTransactionId = parseTransactionId(responseData?.transaction_id)

    if (!parsedTransactionId) {
      return {
        transactionId: '',
        status: 'empty',
        errorDetail: sanitizeSdkErrorMessage(responseData?.error || responseData?.message || 'No transaction_id')
      }
    }

    persistEverflowClickResponse({
      affId,
      offerId,
      responseData,
      transactionId: parsedTransactionId
    })

    return {
      transactionId: parsedTransactionId,
      status: 'success',
      errorDetail: ''
    }
  } catch (error) {
    return {
      transactionId: '',
      status: error?.name === 'AbortError' ? 'timeout' : 'network_error',
      errorDetail: error?.name === 'AbortError'
        ? `Timed out after ${CLICK_TIMEOUT_MS}ms`
        : sanitizeSdkErrorMessage(error?.message || 'Network error')
    }
  }
}

function getStoredEverflowTransactionDetails({ affId, offerId }) {
  const normalizedOfferId = normalizeOfferId(offerId)
  const storedValue = readSessionStorage(getStorageKey({ affId, offerId: normalizedOfferId }))
  if (!storedValue) {
    return {
      transactionId: '',
      source: ''
    }
  }

  try {
    const parsed = JSON.parse(storedValue)
    const isCurrentLocation = parsed.locationSignature === getLocationSignature()
    const isFresh = Date.now() - Number(parsed.storedAt || 0) <= STORED_TRANSACTION_MAX_AGE_MS

    if (!isCurrentLocation || !isFresh) {
      removeSessionStorage(getStorageKey({ affId, offerId: normalizedOfferId }))
      return {
        transactionId: '',
        source: ''
      }
    }

    return {
      transactionId: parseTransactionId(parsed.transactionId),
      source: 'ss'
    }
  } catch {
    removeSessionStorage(getStorageKey({ affId, offerId: normalizedOfferId }))
    return {
      transactionId: '',
      source: ''
    }
  }
}

function recoverEverflowTransactionDetails({ affId, offerId }) {
  const storedTransaction = getStoredEverflowTransactionDetails({ affId, offerId })
  if (storedTransaction.transactionId) {
    return storedTransaction
  }

  if (typeof window !== 'undefined' && window.EF) {
    try {
      const advertiserTransactionId = parseTransactionId(window.EF.getAdvertiserTransactionId?.(affId))
      if (advertiserTransactionId) {
        return {
          transactionId: advertiserTransactionId,
          source: 'ea'
        }
      }

      const offerTransactionId = parseTransactionId(window.EF.getTransactionId?.(offerId))
      if (offerTransactionId) {
        return {
          transactionId: offerTransactionId,
          source: 'eo'
        }
      }
    } catch {
      // Fall back to direct cookie parsing if the SDK object is present but unstable.
    }
  }

  return recoverTransactionIdFromCookies({ affId, offerId })
}

async function pollForRecoveredTransactionId({ affId, offerId, logLabel }) {
  for (let attempt = 0; attempt < CLICK_RECOVERY_ATTEMPTS; attempt++) {
    const recovery = recoverEverflowTransactionDetails({ affId, offerId })

    if (recovery.transactionId) {
      if (logLabel) {
        appendEverflowDebugLog({
          affId,
          offerId,
          entry: `${logLabel}${recovery.source}`
        })
      }

      return recovery.transactionId
    }

    if (attempt < CLICK_RECOVERY_ATTEMPTS - 1) {
      await wait(CLICK_RECOVERY_DELAY_MS)
    }
  }

  if (logLabel) {
    appendEverflowDebugLog({ affId, offerId, entry: `${logLabel}no` })
  }

  return ''
}

export function getStoredEverflowTransactionId({ affId, offerId }) {
  return getStoredEverflowTransactionDetails({ affId, offerId }).transactionId
}

export function persistEverflowTransactionId({ affId, offerId, transactionId }) {
  if (typeof window === 'undefined' || !transactionId) return

  const normalizedOfferId = normalizeOfferId(offerId)

  writeSessionStorage(
    getStorageKey({ affId, offerId: normalizedOfferId }),
    JSON.stringify({
      transactionId,
      locationSignature: getLocationSignature(),
      storedAt: Date.now()
    })
  )
}

export function clearStoredEverflowTransactionId({ affId, offerId }) {
  removeSessionStorage(getStorageKey({ affId, offerId: normalizeOfferId(offerId) }))
}

export function recoverEverflowTransactionId({ affId, offerId }) {
  return recoverEverflowTransactionDetails({ affId, offerId }).transactionId
}

// Initialize Everflow click tracking
export async function initEverflow({ affId, offerId, params, onTransactionId, attemptLabelPrefix = 'p' }) {
  const normalizedOfferId = normalizeOfferId(offerId)
  appendEverflowDebugLog({ affId, offerId: normalizedOfferId, entry: 'i' })
  appendEverflowDebugLog({ affId, offerId: normalizedOfferId, entry: 'lc' })

  const storedTransactionId = getStoredEverflowTransactionId({ affId, offerId: normalizedOfferId })

  if (storedTransactionId) {
    appendEverflowDebugLog({ affId, offerId: normalizedOfferId, entry: 'st' })

    if (onTransactionId) {
      onTransactionId(storedTransactionId)
    }

    return storedTransactionId
  }

  let clickResult = await attemptEverflowClick({
    affId,
    offerId: normalizedOfferId,
    params
  })
  appendEverflowDebugLog({
    affId,
    offerId: normalizedOfferId,
    entry: `c1${mapClickStatusToCode(clickResult.status)}`
  })
  if (clickResult.errorDetail) {
    appendEverflowClickError({
      affId,
      offerId: normalizedOfferId,
      sourceLabel: `${attemptLabelPrefix}c1`,
      error: { message: clickResult.errorDetail }
    })
  }

  let transactionId = clickResult.transactionId

  if (!transactionId) {
    transactionId = await pollForRecoveredTransactionId({
      affId,
      offerId: normalizedOfferId,
      logLabel: 'r1'
    })
  }

  if (!transactionId && clickResult.status !== 'success') {
    await wait(CLICK_RETRY_DELAY_MS)

    clickResult = await attemptEverflowClick({
      affId,
      offerId: normalizedOfferId,
      params
    })
    appendEverflowDebugLog({
      affId,
      offerId: normalizedOfferId,
      entry: `c2${mapClickStatusToCode(clickResult.status)}`
    })
    if (clickResult.errorDetail) {
      appendEverflowClickError({
        affId,
        offerId: normalizedOfferId,
        sourceLabel: `${attemptLabelPrefix}c2`,
        error: { message: clickResult.errorDetail }
      })
    }

    transactionId = clickResult.transactionId

    if (!transactionId) {
      transactionId = await pollForRecoveredTransactionId({
        affId,
        offerId: normalizedOfferId,
        logLabel: 'r2'
      })
    }
  }

  if (transactionId) {
    persistEverflowTransactionId({
      affId,
      offerId: normalizedOfferId,
      transactionId
    })
    appendEverflowDebugLog({ affId, offerId: normalizedOfferId, entry: 'tx' })

    if (onTransactionId) {
      onTransactionId(transactionId)
    }
  }

  if (!transactionId) {
    appendEverflowDebugLog({ affId, offerId: normalizedOfferId, entry: 'ntx' })
  }

  return transactionId || ''
}

// Load LP click tracking script
export function loadLpClickScript({ clickCampaignId, lpS5, lpS2, onRequestId }) {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true

    script.onload = function() {
      if (typeof lp_request_id !== 'undefined' && lp_request_id) {
        if (onRequestId) {
          onRequestId(lp_request_id)
        }
        console.log('LP Request ID:', lp_request_id)
        resolve(lp_request_id)
      } else {
        resolve(null)
      }
    }

    script.src = `https://bluemodomedia.leadspediatrack.com/?c=${clickCampaignId}&t=p&s5=${lpS5}&s2=${lpS2}`
    document.getElementsByTagName('head')[0].appendChild(script)
  })
}

// Initialize Everflow with LP click tracking (combined flow)
export async function initEverflowWithClickTracking({ affId, offerId, clickCampaignId, params, onTransactionId, onRequestId }) {
  // First init Everflow
  const transactionId = await initEverflow({
    affId,
    offerId,
    params,
    onTransactionId
  })

  // If there's a click campaign, load it
  if (clickCampaignId) {
    await loadLpClickScript({
      clickCampaignId,
      lpS5: transactionId || '',
      lpS2: params.get('s2') || params.get('sub2') || '',
      onRequestId
    })
  }

  return transactionId
}

export const everflowService = {
  initEverflow,
  loadLpClickScript,
  initEverflowWithClickTracking
}
